import {getPolygonDimensions, projectTo2DFeetInches} from "./DxfCalculations";
import {AreasTable, ObstacleDetailTable, SegmentDetailsTable, TotalObsTable} from "./DxfTables";
import {addMeasurementLable, addNameLabel, addPointLabels, SegmentDetailLabels} from "./DxfLabels";
import {
    Colors,
    LWPolylineFlags,
    point2d,
    point3d
} from "@tarikjabiri/dxf";

// Main function to draw 2D representation of shapes
export function add2dShapes(dxf, item, Block, i) {
    let obsLabelIndex = 0;

    // Draw the main polygon in 2D inches and get converted points
    const polygon2DInches = drawShapeToBlock(dxf, item.rotated_polygon, Block, {
        addPointLabel: true,
        addMeasurementLabel: true,
        addCircle: true,
        shape: "polygon"
    }, i);

    // Draw dormers as obstacles
    item.dormerData.forEach((data, index) => {
        drawShapeToBlock(dxf, data, Block, {
            isObs: true,
            addName: true,
            addCircle: true,
            shape: "dormer"
        }, obsLabelIndex, i);
        obsLabelIndex++;
    });

    // Draw setbacks
    if (item.setbacks.length > 0) {
        drawShapeToBlock(dxf, item.rotated_setbacks, Block, { shape: "setback" }, obsLabelIndex, i);
        obsLabelIndex++;
    }

    // Draw obstacles
    if (item.obstruction.length > 0) {
        item.rotated_obstruction.forEach((obs, obsIndex) => {
            drawShapeToBlock(dxf, obs.polygon, Block, {
                isObs: true,
                addName: true,
                shape: "obstacle"
            }, obsLabelIndex, i);
            obsLabelIndex++;
        });
    }

    // Draw panels
    if (item.panels.length > 0) {
        item.rotated_panels.forEach((panel, panIndex) => {
            drawShapeToBlock(dxf, panel.polygon, Block, { shape: "panel" }, obsLabelIndex, i);
            obsLabelIndex++;
        });
    }

    // Get the polygon bounds and draw data tables
    let polyData = getPolygonDimensions(polygon2DInches);
    drawTables(item, item.rotated_polygon, item.rotated_obstruction, item.dormerData, item.rotated_panels, polyData, Block);
    return polyData;
}

// Draws data tables related to the polygon and its components
export function drawTables(item, reset2dPolygon, obsPoints, dormerPoints, panelPoints, polyData, Block) {
    let cellHeight = 12;
    let tableStartY = polyData.minY - 50;
    let tableStartX = polyData.minX;
    let segmentColumns = [23, 25, 25];

    // Draw the segment details table and labels
    let [xSpace, numRows] = SegmentDetailsTable(item.label, reset2dPolygon, tableStartX, tableStartY, cellHeight, segmentColumns, Block);
    SegmentDetailLabels(Block, tableStartX, polyData.maxY + 40, item.detail, item.label);

    xSpace += tableStartX;

    // Draw area details table and track max rows
    numRows = Math.max(numRows, AreasTable(item.area_details, xSpace, tableStartY, cellHeight, [50, 55, 57], Block));
    let ySpace = tableStartY - (numRows * cellHeight) - 12;

    // Draw obstacle and total obstacle tables if applicable
    if (item.obstruction.length > 0 || dormerPoints.length > 0) {
        const obstacleColumns = [55, 52, 42, 30, 30];
        ySpace -= ObstacleDetailTable(item.label, [item.obstruction, obsPoints, dormerPoints], tableStartX, ySpace, cellHeight, obstacleColumns, Block);

        if (item.obstruction.length > 0) {
            const cellWidths = [88, 43];
            TotalObsTable(item.obstruction, tableStartX, ySpace, cellHeight, cellWidths, Block);
        }
    }
}

// Draws any polygon-like shape (panel, setback, obstacle, etc.) into the given block
export function drawShapeToBlock(dxf, polygon2d, Block, options = {}, index, segmentIndex, buttonFlag) {
    const {
        addPointLabel = false,
        addMeasurementLabel = false,
        addCircle = false,
        isObs = false,
        addName = false,
        shape = "polygon"
    } = options;

    // Convert to inches and project points
    let inchesPoints = polygon2d.map(point => projectTo2DFeetInches(point));
    
    // Draw polyline based on shape type
    let line = Block.addLWPolyline(inchesPoints, { flags: LWPolylineFlags.Closed });

    // Set line color and linetype based on shape
    if (shape === 'obstacle') {
        line.colorNumber = 232;
    } else if (shape === 'dormer') {
        line.colorNumber = 32;
        // line.lineType = 'ACAD_ISO11W600';
    } else if (shape === "setback") {
        line.colorNumber = 32;
    } else if (shape === "panel") {
        line.colorNumber = Colors.Blue;
    }

    // Add optional labels and measurements
    if (addPointLabel) {
        addPointLabels(inchesPoints, Block);
    }

    if (addMeasurementLabel) {
        addMeasurementLable(polygon2d, inchesPoints, Block);
    }

    if (addName) {
        addNameLabel(inchesPoints, Block, index, isObs);
    }

    // Add circle insert if enabled
    if (addCircle) {
        let circleBlock = setCircleBlocks(dxf, shape, index, segmentIndex, inchesPoints);
        Block.addInsert(circleBlock.name, point3d(0, 0, 0));
    }

    return inchesPoints;
}

// Create and return a circle block based on the shape type
function setCircleBlocks(dxf, shape, index, segmentIndex, inchesPoints) {
    const circleBlock = dxf.addBlock(`CircleBlock_${index}_${segmentIndex}`);

    if (shape === 'dormer') {
        // Dormer circle styling
        // circleBlock.setLayerName('l_seaBlue');
        circleBlock.addCircle(point2d(inchesPoints[0].point.x, inchesPoints[0].point.y), 1.2);
    } else {
        // Default circle styling
        // circleBlock.setLayerName('l_cyan');
        circleBlock.addCircle(point2d(inchesPoints[0].point.x, inchesPoints[0].point.y), 5);
    }

    return circleBlock;
}
